import { useLayoutEffect, useRef, useState } from 'react';

/**
 * useReveal — shared IntersectionObserver reveal-on-scroll hook for the
 * AI Employees landing sections. Returns [ref, isVisible]; attach `ref` to the
 * element and toggle the `is-visible` class on `isVisible` (pairs with the
 * `.aie-reveal` CSS). Disconnects after first reveal.
 *
 * Fail-open guarantee (Jul 30): reveal-on-scroll is a progressive enhancement —
 * it must never be able to KEEP real content invisible. If, `failOpenMs` after
 * mount, the element is already inside the viewport but the observer still has
 * not fired (observed on /ai-organization inside the app shell, where sections
 * stayed at opacity 0 on first paint), we reveal it anyway. Elements genuinely
 * below the fold are untouched — they keep their normal reveal-on-scroll.
 *
 * Fast-scroll guarantee (Aug 3): a fast scroll used to land the visitor on a
 * still-hidden section (blank beat, then a fade-in). Fixes:
 * - The observer's `root` is the element's nearest scrollable ancestor. In the
 *   app shell these pages scroll inside `main.ant-layout-content`, NOT the
 *   window — with the default (viewport) root, the target rect is clipped by
 *   that container, so `rootMargin` lookahead never fired. With the correct
 *   root, `rootMargin` pre-triggers the reveal while the section is still ~60%
 *   of a viewport BELOW the fold: by the time it scrolls in, it's visible.
 *   (In the marketing shell, where the document itself scrolls, there is no
 *   scrollable ancestor and we fall back to the viewport root — same behavior.)
 * - default `threshold` is 0 (any visible pixel counts), so a section can no
 *   longer get STUCK invisible when a scroll stops with only a sliver of it
 *   on screen (with the old 0.18, a section left <18% visible never revealed).
 */

// Nearest ancestor that actually scrolls vertically (the observer root).
// null = no scrollable ancestor -> IntersectionObserver uses the viewport.
// Exported: MobileStickyCta reuses it to find the same scroll container.
export function getScrollParent(el) {
  let p = el.parentElement;
  while (p) {
    const { overflowY } = getComputedStyle(p);
    if ((overflowY === 'auto' || overflowY === 'scroll') && p.scrollHeight > p.clientHeight) {
      return p;
    }
    p = p.parentElement;
  }
  return null;
}

export default function useReveal(threshold = 0, failOpenMs = 1400, rootMargin = '0px 0px 100% 0px') {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  // useLayoutEffect (not useEffect): during the prerender takeover the fresh
  // React render commits opacity-0 over content the visitor is ALREADY seeing;
  // useEffect runs AFTER that frame paints (a visible blink), useLayoutEffect
  // runs before paint, so the takeover reveal below lands in the same frame.
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      // No observer support at all: never gate content.
      setVisible(true);
      return undefined;
    }
    // Prerender takeover (see window.__CC_PRERENDER_TAKEOVER in src/index.js):
    // the static HTML already showed this element if it sits in the first
    // viewport — reveal it immediately instead of re-fading content the
    // visitor is already looking at. Below-fold elements keep their normal
    // reveal-on-scroll.
    if (typeof window !== 'undefined' && window.__CC_PRERENDER_TAKEOVER === true) {
      const r = ref.current.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < vh && r.bottom > 0) {
        setVisible(true);
        return undefined;
      }
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { root: getScrollParent(ref.current), threshold, rootMargin }
    );
    io.observe(ref.current);
    const failOpen = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Any part of the element is inside the viewport but it never revealed:
      // the observer is stuck — fail open. (Below-fold elements keep waiting.)
      if (r.top < vh && r.bottom > 0) {
        setVisible(true);
        io.disconnect();
      }
    }, failOpenMs);
    return () => {
      io.disconnect();
      clearTimeout(failOpen);
    };
  }, [threshold, failOpenMs, rootMargin]);

  return [ref, visible];
}
